import * as dateFns from "date-fns";
import { logger } from "@bfc-software/app-toolbox";
import { Tag, type TagTone, Barcode } from "@bfc-software/pallet";
import { TaskPlannerSchemaTypes } from "@bfc-software/api-schemas";

import { DetailField } from "./detail-field";
import styles from "./task-requirements.module.css";

/**
 * The requirement rendering shared by the internal engine-room task page and
 * the outbound task detail drawer (SR-229 / SR-318).
 *
 * Moved verbatim out of the engine-room route rather than reimplemented,
 * because a second renderer of the same six-variant union would drift from
 * this one the first time a variant's fields change and only one caller got
 * updated. Everything here is presentational - no route params, no
 * `AuthContext`, no query hook, no page-local state - which is what made the
 * move safe.
 *
 * This module deliberately does not call `useTranslation` itself. Translation
 * lives in per-feature namespaces (`locales/en/features/*.json`) with no
 * shared bucket, and the internal page's keys (`page.taskDetail.requirements.*`
 * in `warehouse.json`) and the outbound copies
 * (`tasks.taskDetail.requirements.*` in `outbound.json`) are two different
 * namespaces that `useTranslation` cannot straddle. So every string a caller
 * would otherwise fetch with `t` is instead a field on `RequirementLabels`,
 * built by each caller from its own namespace. `labels.kind` is a
 * `Record<RequirementKind, string>` rather than six separate props for the
 * same reason `REQUIREMENT_TONES` below is a `Record`: a new requirement kind
 * fails to compile at every caller until it supplies a label, which is worth
 * more than not repeating six words of English twice.
 */

export type TaskDetailRequirement =
  TaskPlannerSchemaTypes.components["schemas"]["TaskDetailRequirement"];
export type TaskDetailRequirementRecord =
  TaskPlannerSchemaTypes.components["schemas"]["TaskDetailRequirementRecord"];

export type RequirementKind = TaskDetailRequirement["kind"];

/** Every string the requirement rendering needs, supplied by the caller's own i18n namespace. */
export interface RequirementLabels {
  /** The `RequirementsList` heading. */
  heading: string;
  /** One label per requirement kind - a Record so a new kind fails to build until every caller supplies one. */
  kind: Record<RequirementKind, string>;
  slotId: string;
  slotUpc: string;
  itemId: string;
  itemUpc: string;
  expectedQuantity: string;
  weightMin: string;
  weightMax: string;
  weightUom: string;
  expiryNotBefore: string;
  expiryNotAfter: string;
  exceptions: string;
  catchweightNote: string;
  confirmLotNote: string;
  confirmExpiryNote: string;
}

/**
 * One tone per requirement kind, so a kind is recognisable by colour before the
 * label is read. There are exactly as many kinds as pallet has tones, so the
 * mapping is 1:1 and no two cards in a pick line share a colour.
 *
 * `confirm_expiry` takes `danger` because it is the one requirement whose
 * out-of-range outcome is a shelf-life problem rather than a re-scan.
 */
const REQUIREMENT_TONES: Record<RequirementKind, TagTone> = {
  scan_slot_code: "info",
  scan_item_code: "warning",
  confirm_count: "success",
  confirm_catchweight: "accent",
  confirm_lot: "neutral",
  confirm_expiry: "danger",
};

/**
 * The shell every requirement card shares: the kind Tag, and the tone carried
 * past the tag as a swatch down the card's leading edge. The swatch reads the
 * same role token the Tag paints itself with, so the two always agree.
 */
function RequirementShell({
  kind,
  label,
  children,
}: {
  kind: RequirementKind;
  label: string;
  children?: React.ReactNode;
}) {
  const tone = REQUIREMENT_TONES[kind];

  return (
    <div
      className={styles.requirementCard}
      data-test-role="task-detail-requirement"
      data-requirement-kind={kind}
      data-tone={tone}
      style={
        {
          "--requirement-swatch": `var(--pallet-role-${tone}-container)`,
        } as React.CSSProperties
      }
    >
      <Tag label={label} display={{ tone }} />
      {children}
    </div>
  );
}

/**
 * The exception vocabulary a requirement advertises - the codes the device may
 * report when the operator cannot satisfy it. Server-authored per requirement,
 * so it is rendered as given rather than translated. Kinds with no vocabulary
 * (the two capture requirements) pass nothing and this renders nothing.
 */
function RequirementExceptions({
  codes,
  exceptionsLabel,
}: {
  codes?: readonly string[];
  exceptionsLabel: string;
}) {
  if (!codes || codes.length === 0) {
    return null;
  }

  return (
    <div
      className={styles.requirementExceptions}
      data-test-role="task-detail-requirement-exceptions"
    >
      <span className={styles.detailLabel}>{exceptionsLabel}</span>
      <div className={styles.requirementExceptionCodes}>
        {codes.map((code) => (
          <code key={code} className={styles.requirementExceptionCode}>
            {code}
          </code>
        ))}
      </div>
    </div>
  );
}

/**
 * `valid_range` is carried as date-time but bounds a shelf-life window, so it
 * reads as a date. Anything unparseable falls back to the raw value rather than
 * throwing inside the render.
 */
function formatExpirationBound(value: string): string {
  const parsed = dateFns.parseISO(value);
  return dateFns.isValid(parsed) ? dateFns.format(parsed, "PP") : value;
}

/**
 * Renders a single synthesized requirement (proof action) as a compact card,
 * mirroring the load-location treatment: a kind Tag over a small field grid.
 * The requirement is a discriminated union on `kind`.
 */
function RequirementCard({
  requirement,
  labels,
}: {
  requirement: TaskDetailRequirement;
  labels: RequirementLabels;
}) {
  switch (requirement.kind) {
    case "scan_slot_code":
      return (
        <RequirementShell
          kind={requirement.kind}
          label={labels.kind.scan_slot_code}
        >
          <div className={styles.requirementFields}>
            <DetailField label={labels.slotId} value={requirement.slot_id} />
            <DetailField label={labels.slotUpc} value={requirement.slot_upc} />
          </div>
          <Barcode value={requirement.slot_upc} label={labels.slotUpc} />
          <RequirementExceptions
            codes={requirement.valid_slot_scan_exceptions}
            exceptionsLabel={labels.exceptions}
          />
        </RequirementShell>
      );
    case "scan_item_code": {
      const barcodes = requirement.item_upcs.map((upc) => {
        return (
          <Barcode
            key={`barcode-upc-${requirement.id}-${upc}`}
            label={labels.itemUpc}
            value={upc}
          />
        );
      });

      const upcs = requirement.item_upcs.map((upc) => {
        return (
          <DetailField
            key={`item-upc-${requirement.id}-${upc}`}
            label={labels.itemUpc}
            value={upc}
          />
        );
      });

      return (
        <RequirementShell
          kind={requirement.kind}
          label={labels.kind.scan_item_code}
        >
          <div className={styles.requirementFields}>
            <DetailField label={labels.itemId} value={requirement.item_id} />
            {upcs}
          </div>
          <div>{barcodes}</div>
          <RequirementExceptions
            codes={requirement.valid_item_scan_exceptions}
            exceptionsLabel={labels.exceptions}
          />
        </RequirementShell>
      );
    }
    case "confirm_count":
      return (
        <RequirementShell
          kind={requirement.kind}
          label={labels.kind.confirm_count}
        >
          <div className={styles.requirementFields}>
            <DetailField
              label={labels.expectedQuantity}
              value={requirement.expected_quantity}
            />
          </div>
          <RequirementExceptions
            codes={requirement.valid_count_confirmation_exceptions}
            exceptionsLabel={labels.exceptions}
          />
        </RequirementShell>
      );
    case "confirm_catchweight":
      return (
        <RequirementShell
          kind={requirement.kind}
          label={labels.kind.confirm_catchweight}
        >
          <div className={styles.requirementFields}>
            <DetailField
              label={labels.weightMin}
              value={requirement.weight_min}
            />
            <DetailField
              label={labels.weightMax}
              value={requirement.weight_max}
            />
            <DetailField
              label={labels.weightUom}
              value={requirement.unit_of_measure}
            />
          </div>
          {/* No exception vocabulary by design: a weight confirmation always
              satisfies this requirement, and the service derives in/out of
              range from the weighed value itself. */}
          <p className={styles.requirementNote}>{labels.catchweightNote}</p>
        </RequirementShell>
      );
    case "confirm_lot":
      // Child-less: the requirement row carries nothing beyond its id, so the
      // card states what the device must capture and stops there.
      return (
        <RequirementShell
          kind={requirement.kind}
          label={labels.kind.confirm_lot}
        >
          <p className={styles.requirementNote}>{labels.confirmLotNote}</p>
        </RequirementShell>
      );
    case "confirm_expiry":
      return (
        <RequirementShell
          kind={requirement.kind}
          label={labels.kind.confirm_expiry}
        >
          <div className={styles.requirementFields}>
            <DetailField
              label={labels.expiryNotBefore}
              value={formatExpirationBound(requirement.valid_range.not_before)}
            />
            <DetailField
              label={labels.expiryNotAfter}
              value={formatExpirationBound(requirement.valid_range.not_after)}
            />
          </div>
          <p className={styles.requirementNote}>{labels.confirmExpiryNote}</p>
        </RequirementShell>
      );
    default: {
      // Every kind in the union is handled above, so this is only reachable
      // when the service starts sending a variant this build predates.
      const unhandled: never = requirement;
      logger.warn(
        `Unrecognized requirement type '${(unhandled as { kind: string }).kind}'`,
      );
      return null;
    }
  }
}

/**
 * A pick line's requirements, as a heading over a wrapped row of cards.
 *
 * Thin on purpose: grouping requirements by pick line and deciding whether a
 * given pick has any at all is page-specific (a `requirementsByPick` lookup
 * built from route state), so it stays in each caller's route rather than
 * here - this only ever draws the list it is handed.
 *
 * `requirements` asks only for `id` and `requirement` - the two fields this
 * ever reads - rather than the full `TaskDetailRequirementRecord`. The
 * engine-room caller groups the raw wire records directly and can still pass
 * one of those arrays straight through; the outbound drawer's `RequirementRecord`
 * (`tasks/-data/types.ts`) renames the wire's `task_detail_pick_id` to `pickId`
 * for its join and would fail to satisfy the stricter type for a field neither
 * caller's render path touches.
 */
export function RequirementsList({
  requirements,
  labels,
}: {
  requirements: ReadonlyArray<
    Pick<TaskDetailRequirementRecord, "id" | "requirement">
  >;
  labels: RequirementLabels;
}) {
  return (
    <div
      className={styles.requirements}
      data-test-role="task-detail-requirements"
    >
      <span className={styles.detailLabel}>{labels.heading}</span>
      <div className={styles.requirementsList}>
        {requirements.map((record) => {
          return (
            <RequirementCard
              key={record.id}
              requirement={record.requirement}
              labels={labels}
            />
          );
        })}
      </div>
    </div>
  );
}
